/* ==========================================================================
   Yum's — storefront behaviour

   Two halves:
   1. Presentation — scroll reveal, header shrink, mobile menu, FAQ accordion.
   2. Cart — the "− N in order +" steppers, backed by the real Shopify cart so
      checkout, COD, inventory and order confirmation all stay native.
   ========================================================================== */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var ROOT =
    window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';

  /* ==================================================================== */
  /* 1. Presentation                                                       */
  /* ==================================================================== */

  /* -- Scroll reveal --------------------------------------------------- */
  function initReveal(scope) {
    var items = (scope || document).querySelectorAll('.reveal:not(.is-in)');
    if (!items.length) return;

    if (reduced || !('IntersectionObserver' in window)) {
      items.forEach(function (el) {
        el.classList.add('is-in');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.08 }
    );

    items.forEach(function (el) {
      // Stagger siblings so groups cascade instead of popping together.
      if (!el.style.getPropertyValue('--d')) {
        var group = el.closest('[data-stagger]');
        if (group) {
          var peers = Array.prototype.slice.call(group.querySelectorAll('.reveal'));
          el.style.setProperty('--d', peers.indexOf(el) * 90 + 'ms');
        }
      }
      observer.observe(el);
    });
  }

  /* -- Header shrink --------------------------------------------------- */
  function initHeader() {
    var header = document.querySelector('.y-header');
    if (!header) return;
    var onScroll = function () {
      header.classList.toggle('is-stuck', window.scrollY > 12);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* -- Mobile menu ----------------------------------------------------- */
  function initMenu() {
    var toggle = document.querySelector('.y-nav__toggle');
    var menu = document.querySelector('.y-mobile-menu');
    if (!toggle || !menu) return;

    var setMenu = function (open) {
      toggle.setAttribute('aria-expanded', String(open));
      menu.classList.toggle('is-open', open);
    };

    toggle.addEventListener('click', function () {
      setMenu(toggle.getAttribute('aria-expanded') !== 'true');
    });
    menu.addEventListener('click', function (e) {
      if (e.target.closest('a')) setMenu(false);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') setMenu(false);
    });
  }

  /* -- FAQ accordion ---------------------------------------------------- */
  /* <details> snaps open with no transition, so drive the height by hand
     while keeping the real open/closed state for semantics and a11y.     */
  function initFaq(scope) {
    (scope || document).querySelectorAll('.y-faq details').forEach(function (item) {
      if (item.dataset.bound) return;
      item.dataset.bound = '1';

      var summary = item.querySelector('summary');
      var body = item.querySelector('.y-faq__body');
      if (!summary || !body) return;

      var animating = false;

      function settle(after) {
        function done(e) {
          if (e.propertyName !== 'height') return;
          body.removeEventListener('transitionend', done);
          body.style.height = '';
          animating = false;
          if (after) after();
        }
        body.addEventListener('transitionend', done);
      }

      summary.addEventListener('click', function (e) {
        e.preventDefault();
        if (reduced) {
          item.open = !item.open;
          return;
        }
        if (animating) return;
        animating = true;

        if (item.open) {
          body.style.height = body.scrollHeight + 'px';
          settle(function () {
            item.open = false;
          });
          requestAnimationFrame(function () {
            requestAnimationFrame(function () {
              body.style.height = '0px';
            });
          });
        } else {
          item.open = true;
          var target = body.scrollHeight;
          body.style.height = '0px';
          settle(null);
          requestAnimationFrame(function () {
            requestAnimationFrame(function () {
              body.style.height = target + 'px';
            });
          });
        }
      });
    });
  }

  /* ==================================================================== */
  /* 2. Cart                                                               */
  /* ==================================================================== */

  function sectionIds() {
    var ids = [];
    document.querySelectorAll('[data-cart-section]').forEach(function (el) {
      var id = el.getAttribute('data-cart-section');
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  }

  function postJSON(url, body) {
    return fetch(ROOT.replace(/\/$/, '') + url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) throw new Error(data.description || data.message || 'Cart error');
        return data;
      });
    });
  }

  /* Swap in freshly rendered section markup from the Section Rendering API. */
  function applySections(sections) {
    if (!sections) return;
    Object.keys(sections).forEach(function (id) {
      var markup = sections[id];
      if (typeof markup !== 'string') return;
      var parsed = new DOMParser().parseFromString(markup, 'text/html');
      var fresh = parsed.querySelector('[data-cart-section="' + id + '"]');
      var current = document.querySelector('[data-cart-section="' + id + '"]');
      if (fresh && current) {
        current.innerHTML = fresh.innerHTML;
        // Re-arm behaviour inside the replaced markup.
        current.querySelectorAll('.reveal').forEach(function (el) {
          el.classList.add('is-in');
        });
        initFaq(current);
      }
    });
  }

  function updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  function setBusy(node, busy) {
    var stepper = node && node.closest ? node.closest('.y-stepper') : null;
    if (stepper) stepper.classList.toggle('is-busy', !!busy);
  }

  function showError(message) {
    var box = document.querySelector('[data-cart-error]');
    if (!box) {
      window.alert(message);
      return;
    }
    box.textContent = message;
    box.hidden = false;
  }

  function clearError() {
    var box = document.querySelector('[data-cart-error]');
    if (box) {
      box.textContent = '';
      box.hidden = true;
    }
  }

  function mutate(node, action, payload) {
    clearError();
    setBusy(node, true);
    payload.sections = sectionIds().join(',');
    payload.sections_url = window.location.pathname + window.location.search;

    return postJSON(action, payload)
      .then(function (data) {
        applySections(data.sections);
        // /cart/add.js returns the line item; /cart/change.js returns the cart.
        if (typeof data.item_count === 'number') {
          updateCartCount(data.item_count);
        } else {
          fetch(ROOT.replace(/\/$/, '') + '/cart.js')
            .then(function (r) { return r.json(); })
            .then(function (cart) { updateCartCount(cart.item_count); })
            .catch(function () {});
        }
        document.dispatchEvent(new CustomEvent('yums:cart:updated', { detail: data }));
      })
      .catch(function (err) {
        showError(err.message || 'Something went wrong. Please try again.');
      })
      .then(function () {
        setBusy(node, false);
      });
  }

  /* Delegated so it keeps working across re-rendered sections. */
  document.addEventListener('click', function (event) {
    var btn = event.target.closest('[data-cart-add], [data-cart-minus], [data-cart-remove]');
    if (!btn) return;

    var variantId = btn.getAttribute('data-variant-id');
    var lineKey = btn.getAttribute('data-line-key');
    var quantity = parseInt(btn.getAttribute('data-quantity') || '0', 10);

    if (btn.hasAttribute('data-cart-add')) {
      event.preventDefault();
      if (!variantId) return;
      mutate(btn, '/cart/add.js', { items: [{ id: Number(variantId), quantity: 1 }] });
      return;
    }

    if (btn.hasAttribute('data-cart-minus')) {
      event.preventDefault();
      if (quantity <= 0) return;
      mutate(btn, '/cart/change.js', { id: lineKey || Number(variantId), quantity: quantity - 1 });
      return;
    }

    if (btn.hasAttribute('data-cart-remove')) {
      event.preventDefault();
      mutate(btn, '/cart/change.js', { id: lineKey || Number(variantId), quantity: 0 });
    }
  });

  /* Product page: keep variant picker, price and button in sync. */
  document.addEventListener('change', function (event) {
    var select = event.target.closest('[data-variant-select]');
    if (!select) return;

    var form = select.closest('form');
    if (!form) return;

    var option = select.options[select.selectedIndex];
    var idInput = form.querySelector('[data-variant-id-input]');
    if (idInput) idInput.value = select.value;

    var priceEl = document.querySelector('[data-variant-price]');
    if (priceEl && option.dataset.price) priceEl.textContent = option.dataset.price;

    var submit = form.querySelector('[type="submit"]');
    if (submit) {
      var available = option.dataset.available === 'true';
      submit.disabled = !available;
      submit.textContent = available ? submit.dataset.labelAvailable : submit.dataset.labelSoldOut;
    }
  });

  /* ==================================================================== */
  /* Boot                                                                  */
  /* ==================================================================== */
  function boot() {
    initReveal();
    initHeader();
    initMenu();
    initFaq();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor: re-arm after a section is re-rendered.
  document.addEventListener('shopify:section:load', function (e) {
    initReveal(e.target);
    initFaq(e.target);
    initHeader();
    initMenu();
  });
})();
